import type { ZonedMoment } from '@der-maschinfuehrer/contracts';
import { useEffect, useId, useRef, useState } from 'react';

import {
  localDateTimeInputValue,
  zonedMomentChoicesFromLocalInput,
  zonedMomentNow,
  zonedMomentToLocalInput,
} from '../domain/zoned-moment';

export function OptionalZonedMomentField({
  disabled = false,
  label,
  name,
  onChange,
  value,
}: {
  readonly disabled?: boolean;
  readonly label: string;
  readonly name: string;
  readonly onChange: (value: ZonedMoment | null) => void;
  readonly value: ZonedMoment | null;
}) {
  const generatedId = useId();
  const inputId = `${name}-${generatedId}`;
  const descriptionId = `${inputId}-description`;
  const input = useRef<HTMLInputElement>(null);
  const lastEmittedValueKey = useRef(zonedMomentKey(value));
  const [enabled, setEnabled] = useState(value !== null);
  const [rawValue, setRawValue] = useState(value === null ? '' : zonedMomentToLocalInput(value));
  const [choices, setChoices] = useState<readonly ZonedMoment[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    input.current?.setCustomValidity(error ?? '');
  }, [error]);

  const valueKey = zonedMomentKey(value);
  useEffect(() => {
    if (valueKey === lastEmittedValueKey.current) return;
    lastEmittedValueKey.current = valueKey;
    setEnabled(value !== null);
    setRawValue(value === null ? '' : zonedMomentToLocalInput(value));
    setChoices([]);
    setError(null);
  }, [value, valueKey]);

  const emitChange = (next: ZonedMoment | null) => {
    lastEmittedValueKey.current = zonedMomentKey(next);
    onChange(next);
  };

  const updateRawValue = (nextRaw: string) => {
    setRawValue(nextRaw);
    setChoices([]);
    if (nextRaw === '') {
      setError('Vyplň místní datum a čas, nebo volitelný čas odeber.');
      emitChange(null);
      return;
    }
    try {
      const nextChoices = zonedMomentChoicesFromLocalInput(nextRaw);
      if (nextChoices.length === 0) {
        setError('Tento místní čas v aktuální časové zóně neexistuje.');
        emitChange(null);
      } else if (nextChoices.length === 1) {
        setError(null);
        emitChange(nextChoices[0] ?? null);
      } else {
        setChoices(nextChoices);
        setError('Místní čas nastal dvakrát. Vyber správný UTC offset.');
        emitChange(null);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Čas není platný.');
      emitChange(null);
    }
  };

  const useNow = () => {
    const now = new Date();
    const moment = zonedMomentNow(now);
    setEnabled(true);
    setRawValue(localDateTimeInputValue(now));
    setChoices([]);
    setError(null);
    emitChange(moment);
  };

  if (!enabled) {
    return (
      <div className="optional-time-field">
        <span>{label}: neuvedeno</span>
        <div className="inline-actions">
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={disabled}
            onClick={() => {
              setEnabled(true);
              setError('Vyplň místní datum a čas, nebo volitelný čas odeber.');
            }}
          >
            Přidat {label.toLocaleLowerCase('cs-CZ')}
          </button>
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={disabled}
            onClick={useNow}
          >
            {label}: Teď
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-field optional-time-field">
      <label htmlFor={inputId}>{label}</label>
      <p id={descriptionId} className="field-description">
        Ukládá se místní čas, IANA časová zóna a konkrétní UTC offset. Nic se nedoplní samo.
      </p>
      <input
        ref={input}
        id={inputId}
        name={name}
        className="text-input touch-target"
        type="datetime-local"
        value={rawValue}
        disabled={disabled}
        required
        aria-describedby={descriptionId}
        aria-invalid={error === null ? undefined : true}
        onChange={(event) => updateRawValue(event.currentTarget.value)}
      />
      {choices.length > 1 ? (
        <label className="form-field">
          <span>UTC offset pro {label.toLocaleLowerCase('cs-CZ')}</span>
          <select
            className="text-input touch-target"
            defaultValue=""
            required
            disabled={disabled}
            onChange={(event) => {
              const selected = choices.find(
                (choice) => choice.utc_offset === event.currentTarget.value,
              );
              if (selected === undefined) {
                setError('Vyber konkrétní UTC offset.');
                emitChange(null);
              } else {
                setError(null);
                emitChange(selected);
              }
            }}
          >
            <option value="">Vyber offset</option>
            {choices.map((choice) => (
              <option key={choice.instant} value={choice.utc_offset}>
                {choice.utc_offset} · {choice.time_zone}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="inline-actions">
        <button
          type="button"
          className="secondary-action touch-target"
          disabled={disabled}
          onClick={useNow}
        >
          Teď
        </button>
        <button
          type="button"
          className="secondary-action touch-target"
          disabled={disabled}
          onClick={() => {
            setEnabled(false);
            setRawValue('');
            setChoices([]);
            setError(null);
            emitChange(null);
          }}
        >
          Odebrat čas
        </button>
      </div>
    </div>
  );
}

function zonedMomentKey(value: ZonedMoment | null): string {
  return value === null
    ? 'null'
    : `${value.instant}\u0000${value.time_zone}\u0000${value.utc_offset}`;
}
